import {
  EBadgePosition,
  EDailyBonusInternalViewModel,
  EFontWeights,
  Gradient
} from '@appcharge/shared-ui';
import {
  BodyBlockId,
  BodyBlockV2Item,
  OnePlusManyInstanceId
} from 'constants/bodyBlocks';
import {
  EAppearanceStatus,
  EAwardFlowStatus,
  EEventCategories,
  EEventSubCategories,
  EPendingAppearanceStatus,
  EPopupSubType,
  EProgressBarType,
  EPosition,
  EProductPriority,
  EProductType,
  ERewardCalendarMissedReset,
  ERewardCalendarPopUpTrigger,
  ERewardCalendarRewardCollection,
  ESaleDiscountDisplayType,
  EScheduleType,
  ESectionOfferType,
  OfferType,
  StoreBodyBlock
} from 'constants/enums';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import { FormikErrors, FormikTouched, FormikValues } from 'formik';
import type { ProductProps } from 'views/CheckoutLinks/CheckoutLinkForm';
import {
  EPagesRadioButtonOptions,
  EPagesTemplateOptions,
  EPagesVisibilityOptions,
  IBlock
} from 'views/Pages/PagesForm/constants';

import {
  ETemplateStudioBundlesViewModel,
  ETemplateStudioLayoutOptions,
  EThemes
} from 'components/TemplatesStudio/enums';

import { GradientDirection } from '../components/AcGradientInput/AcGraidentInput.types';
import {
  SectionsItem,
  TabsItem
} from '../components/Theme/StoreTheme/storeTheme.types';

import { IPermissions } from './permissions.types';
import type { TStoreBlock } from './TemplateStudio.types';

export enum UserRole {
  SUPER_ADMIN = 'Super Administrator',
  ADMIN = 'Administrator',
  DEVELOPER = 'Developer',
  PROJECT_OWNER = 'Project Owner',
  SUPPORT = 'Support',
  FINANCE = 'Finance',
  OPERATIONS_EDITOR = 'Editor'
}

export interface UserRoleV2 {
  roleId: string;
  roleName: string;
}

export enum EUserTypeId {
  APPCHARGE = 'Appcharge'
}

export interface Project {
  publisherId: string;
  publisherName: string;
  publisherLogo: string;
  thumbnailImageUrl?: string;
  role: string;
  projectType?: EPublisherSolutionModel;
  projectSubType?: EPublisherSolutionModelSubType;
  permissions?: IPermissions;
  isOnboarding?: boolean;
  tags?: string[];
  themes?: EThemes[];
}

export interface Portal {
  companyId: string;
  portalId: string;
  portalName: string;
  portalDomain: string;
  thumbnailImageUrl?: string;
  role: string;
  isParkingDomain?: boolean;
  stores: Store[];
  tags?: string[];
}

export interface User {
  _id: string;
  userId: string;
  firstName?: string;
  publisherId: string;
  groupId: string;
  typeId: string;
  email: string;
  name: string;
  password: string;
  active: boolean;
  isUserPending?: boolean;
  createdAt: Date;
  updatedAt: Date;
  role: UserRole;
  projects?: Project[];
  portals?: Portal[];
  version?: number;
  company?: string;
  companyDisplayName?: string;
  isOnboarding?: boolean;
}

export interface Image {
  createdAt?: string;
  name: string;
  publisherId: string;
  type: string;
  updatedAt?: string;
  url: string;
  _id: string;
}

export interface OrderDetails {
  _id: string;
  orderId: string;
  playerId: string;
  bundleSku: string;
  bundleName: string;
  receiptId: string;
  bundleId: string;
  createdAt: string;
  currency: string;
  status: string;
  currencySymbol: string;
  providerPaymentId: string;
  publisherPurchaseId?: string;
  amountInDollar: Decimal;
  paymentResolution: string;
  taxRatePoints: Decimal;
  taxTotal: Decimal;
  amountNetInDollar: Decimal;
  amountNetTotal: Decimal;
  estimatedAppchargeFee: number;
  estimatedPublisherNetAmount: number;
  type: string;
  products: {
    sku: string;
    name: string;
    quantity: number;
  }[];
  history: {
    status: string;
    createdAt: string;
    event: string;
  }[];
  paymentMethod: {
    primaryMethod: string;
    secondaryMethod?: string;
  };
  refundThresholdDays: number;
  dispute: Dispute;
  purchaseId?: string;
  playerGeo: {
    countryCode2: string;
    state: string;
    city: string;
    timezone: string;
    latitude: number;
    longitude: number;
    zipCode: string;
    createdByIp: string;
  };
  playerEmail?: string;
  subscriptionId?: string;
  isFirstSubscriptionPayment?: boolean;
  offerSnapshot: {
    currencyCode: string;
    description: string;
    mixpanelSessionId: string;
    name: string;
    price: number;
    priceInUsdCents: number;
    priceInUsd: number;
    offerId: string;
    publisherId: string;
    sequenceIndex: number;
    sequenceNextIndex: number;
    sku: string;
    subType: string;
    type: string;
    sessionMetadata?: Record<string, any>;
  };
  publisherId: string;
  state?: string;
  zipCode?: string;
  priceInUsd: number;
  taxAmountInDollar: Decimal;
  currencyExchangeCost: Decimal;
  discountAmount: Decimal;
  discountAmountInDollar: Decimal;
  discountRatePoints: Decimal;
  promoCodeName: string;
  bundlePrice: number;
  baseNetPrice: number;
  awardFlow: EAwardFlowStatus;
  amountTotal: Decimal;
}

export interface Currency {
  _id: string;
  codePlusSymbol: string;
  createdAt: string;
  currencyCode: string;
  currencyExchangeRate: number;
  currencyName: string;
  currencySymbol: string;
  fractionalSeparator: string;
  isCents: boolean;
  maxChargeAmount: number;
  minChargeAmount: number;
  milSeparator: string;
  multiplier: number;
  rate: number;
  spacing: boolean;
  symbolPosition: 'Left' | 'Right';
  updatedAt: string;
}

export enum DisputeStatus {
  EVIDENCE_SUBMITTED = 'evidence_submitted',
  LOST = 'lost',
  NEEDS_RESPONSE = 'needs_response',
  UNDER_REVIEW = 'under_review',
  WARNING_CLOSED = 'warning_closed',
  WARNING_NEEDS_RESPONSE = 'warning_needs_response',
  WARNING_UNDER_REVIEW = 'warning_under_review',
  WON = 'won'
}

export interface Dispute {
  _id: string;
  publisherId: string;
  status: DisputeStatus;
  evidences: IDisputeEvidence[];
}

export const DisputeEvidenceType = {
  RECEIPT: 'Receipt',
  ACTIVITY_LOGS: 'Activity Logs',
  PRODUCT_USAGE: 'Product Usage',
  CUSTOMER_COMMUNICATION: 'Customer Communication',
  PLAYER_PROFILE: 'Player Profile',
  PLAYER_BILLING_ADDRESS: 'Player Billing Address',
  PAYMENTS_HISTORY: 'Payments History',
  REFUND_CANCELLATION_POLICY: 'Refund & Cancellation Policy',
  REFUND_REFUSAL_EXPLANATION: 'Refund Refusal Explanation',
  IP_ADDRESS_VERIFICATION: 'IP Address Verification',
  OTHER: 'Other'
} as const;

export type DisputeEvidenceType =
  (typeof DisputeEvidenceType)[keyof typeof DisputeEvidenceType];

export interface IDisputeEvidence {
  name: string;
  description: string;
  username: string;
  createdAt: string;
  updatedAt: string;
  _id: string;
  type?: DisputeEvidenceType;
  file?: File;
}

export interface IDisputeEvidenceResponse
  extends Omit<IDisputeEvidence, 'file'> {
  filePath?: string;
}

interface SpecialOffer {
  templateType: string;
  presentOfferEndTimer: boolean;
  title: string;
  fontSize: number;
  fontWeight: EFontWeights;
  fontColor: {
    colorOne: string;
    colorTwo: string;
    direction: string;
  };
  subTitle: Title;
  backgroundColor?: Gradient;
  collectText?: string;
}

export interface RollingOffer {
  arrowColor: string;
  arrowImage?: string;
  headerImage?: string;
  backgroundOpacity: number;
  backgroundColor: Gradient;
  collectBehavior: string;
  subRollingOffer: SubRollingOfferUi;
  enableScroll?: boolean;
  scrollArrowImage?: string;
}

export type FreeTilesLayout = '1-in-a-row' | '2-in-a-row' | '3-in-a-row';

export interface BuyOneGetManyOfferUi {
  headerImage?: string;
  plusImage?: string;
  freeTilesLayout?: FreeTilesLayout;
  collectText?: string;
  backgroundOpacity?: number;
  subOffer: SubBuyOneGetManyOfferUi;
}

interface OfferFields {
  publisherOfferId?: string;
  offerUiId: string;
  name?: string;
  productSale?: number;
  priceDiscount?: number;
  price?: number;
  priority?: number;
  playerAvailability?: number;
  startHour?: string;
  playerClickedTtl?: number;
  interval?: string;
  displayName?: string;
}

interface OfferUiFields {
  externalId: string;
  name: string;
  specialOffer: SpecialOffer;
  rollingOffer: RollingOffer;
  progressBar: ProgressBar;
  borderWidth: number;
  borderColor: Gradient;
  backgroundImage: string;
  horizontalBackgroundImage?: string;
  badgeCoverImage?: string;
  buttonSuffixImage?: string;
  buttonSuffixAnimation?: string;
}
export interface FormikProps {
  values: FormikValues;
  handleChange: (e?: any) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
  touched: FormikTouched<OfferUiFields & OfferFields>;
  errors: FormikErrors<OfferUiFields & OfferFields>;
  setFieldValue: Function;
  isValid?: boolean;
  dirty?: boolean;
  submitForm?: () => void;
  validateField?: (field: string) => void;
}

export interface OfferUiProps extends FormikProps {
  addPictureInputRef?: any;
  getImages?: any;
  setCurrentUploadingField?: any;
  currentUploadingField?: any;
  dup?: boolean;
  isNameUnique?: boolean;
  isExternalIdUnique?: boolean;
  offerDesignTypesItems?: any;
  getAssetUploadMessage?: any;
  setOfferUiType?: (type: string) => void;
}

export interface SubRollingOfferUi {
  backgroundColor?: {
    colorOne: string;
    colorTwo?: string;
    direction?: string;
  };
  backgroundImage?: string;
  lockImage?: string;
  collectText?: string;
}

export interface SubBuyOneGetManyOfferUi {
  backgroundColor?: {
    colorOne: string;
    colorTwo?: string;
    direction?: string;
  };
  lockImage?: string;
  backgroundImage?: string;
  freeTilesBackgroundImage?: string;
  claimedImage?: string;
}

export interface ProgressBarTextStyle {
  text?: string;
  fontSize?: number;
  fontWeight?: string;
  fontColor?: Gradient;
}

export interface ProgressBar {
  type?: EProgressBarType;
  barFillColor: Gradient;
  barRadius: number;
  accumulatedItem: {
    imageUrl: string;
    backgroundColor?: Gradient;
    backgroundImageUrl?: string;
  };
  backgroundOpacity: number;
  backgroundColor: Gradient;
  frameRadius: number;
  textColor?: string;
  barBackgroundImage?: string;
  headerImage?: string;
  title?: ProgressBarTextStyle;
  subtitle?: ProgressBarTextStyle;
  collectedIconImage?: string;
  tooltipBackgroundImage?: string;
}

export interface RewardCalendar {
  backgroundColor: string;
  dayFontColor: string;
  headerImage?: string;
  collectibleBadgeImage?: string;
  lockedBadgeImage?: string;
  subRewardBackgroundImage?: string;
  mainRewardBackgroundImage?: string;
  collectIconImage?: string;
  lockedIconImage?: string;
}

export interface OfferUI {
  _id?: string;
  active: boolean;
  offerUiType: string;
  name: string;
  description: string;
  backgroundImage: string;
  horizontalBackgroundImage?: string;
  badgeCoverImage?: string;
  buttonSuffixImage?: string;
  buttonSuffixAnimation?: string;
  externalId: string;
  specialOffer?: SpecialOffer;
  rollingOffer?: RollingOffer;
  onePlusMany?: BuyOneGetManyOfferUi;
  progressBar?: ProgressBar;
  rewardCalendar?: RewardCalendar;
  popup?: {
    templateType: string;
  };
  offerUiSubType?: string;
  borderColor?: Gradient;
  backgroundColor?: Gradient;
  borderWidth?: number;
  backgroundOpacity?: number;
}

export enum EBadgeType {
  RIBBON = 'ribbon',
  EMBLEM = 'emblem'
}

export interface ColorDto {
  gradientDirection?: GradientDirection;
  colorOne: string;
  colorTwo?: string;
}

export type EmblemType = {
  imageUrl: string;
};

export type RibbonType = {
  text: string;
  backgroundColor: ColorDto;
  textColor: ColorDto;
  imageUrl?: string;
};
export interface Badge {
  badgeId?: string;
  name: string;
  type: EBadgeType;
  publisherBadgeId?: string;
  position: EBadgePosition;
  emblem?: EmblemType;
  ribbon?: RibbonType;
}

export interface Page {
  pageName: string;
  pageIcon: string;
  slug?: string;
  pageTemplate: string;
  metadataImage?: string;
  metadataDescription?: string;
  state: 'hidden' | 'visible' | 'scheduled';
  publishDate?: string;
  blocksV2?: any[];
}

export interface SelfServeSignup {
  email: string;
  name: string;
  password: string;
}

export interface SelfServeSignupGoogle {
  token: string;
  name: string;
}

export interface BundleBadge {
  content: string;
  key: string;
  name: string;
  position: string;
  url?: string;
  value: string;
}

export interface Appearance {
  startDate: string;
  endDate: string;
}

export interface ProductsSequenceProduct {
  product?: string | ProductProps;
  productId?: string;
  quantity?: number | string;
  images?: Record<string, string>;
  publisherProductId?: string;
  priority?: string;
  quantityDisplay?: string;
  strokedSaleQuantity?: string;
  itemClassName?: string;
  productType?: EProductType;
  name?: string;
  displayName?: string;
  textFontColorHex?: string;
  type?: EProductType;
  prefix?: string;
  suffix?: string;
  index?: number;
  rarityProductInfo?: any;
  badgeText?: string;
  salePercentage?: number;
  itemRef?: any;
  numberOfProducts?: number;
  salePercentageDisplayType?: ESaleDiscountDisplayType;
  isFullWidth?: boolean;
  transformValue?: number;
  leftPositionValue?: number;
  className?: string;
  tooltipOpenEventHandler?: () => void;
  toggleTooltip?: (e: React.MouseEvent<HTMLElement>) => void;
  tooltipIcon?: string;
  description?: string;
}

export interface ProductsSequence {
  index: number;
  products: ProductsSequenceProduct[];
  priceInUsdCents: number;
  playerAvailability?: number | null;
  hidePlayerAvailability?: boolean;
  playerClickedTtl?: number;
  barSize?: number;
  progressBarPoints?: ProgressBarPoint[];
}

export interface ProgressBarPoint {
  barId?: string;
  publisherBarId?: string;
  points: number;
}
export interface ImageDetails {
  url: string;
  type: string;
}

export interface PopUpProductDetails {
  productId?: string;
  images?: ImageDetails[];
  name?: string;
  textFontColorHex?: string;
  prefix?: string;
  suffix?: string;
  priority?: string;
  type?: string;
  publisherProductId?: string;
  displayName?: string;
}

export interface PopUpSequenceProduct {
  product?: PopUpProductDetails;
  quantity?: number | string;
  quantityDisplay?: string;
}

export interface PopUpProductSequence {
  index: number;
  products: PopUpSequenceProduct[];
  priceInUsdCents: number;
  playerAvailability?: number;
  hidePlayerAvailability?: boolean;
  id?: string;
  progressBarPoints?: ProgressBarPoint[];
}

export interface Prices {
  priceInUsdCents: number;
  priceDisplay: string;
  strokedPriceDisplay?: string;
  currencyCode?: string;
}

interface RollingOfferProductDetails {
  productId?: string;
  images?: ImageDetails[];
  name?: string;
  textFontColorHex?: string;
  prefix?: string;
  suffix?: string;
  priority?: string;
  type?: string;
  publisherProductId?: string;
}

export interface RollingSequenceProduct {
  product?: RollingOfferProductDetails;
  productId?: number | string;
  quantity?: number | string;
  publisherProductId?: string;
  strokedSaleQuantity?: string;
  quantityDisplay?: string;
  priority?: EProductPriority;
}

export interface RollingOfferProductSequence {
  index: number;
  products: RollingSequenceProduct[];
  priceInUsdCents: number;
  playerAvailability?: number;
  hidePlayerAvailability?: boolean;
  setAsFree?: boolean;
  prices?: Prices;
  id?: string;
  priceDiscount?: PriceDiscount;
  productSale?: ProductSale;
  badges?: RollingOfferBadge[];
  displayName?: string;
  progressBarPoints?: ProgressBarPoint[];
}

export interface RollingOfferBadge {
  badge?: Badge;
}

export interface OfferBadge {
  salesPercentage?: number;
  badgeId: string;
  badge?: Badge;
  publisherBadgeId: string;
  position?: EBadgePosition;
}

export interface PriceDiscount {
  priceBeforeDiscount?: number | null;
  type?: ESaleDiscountDisplayType;
  discount?: number | null;
}

export interface ProductSale {
  amountBeforeSale?: number | null;
  type?: ESaleDiscountDisplayType;
  sale?: number | null;
}

export interface TriggerRule {
  fieldName: string;
  operator: string;
  value: string[];
}

export interface TriggersObject {
  type: string;
  eventName: string;
  every: number;
  rules?: TriggerRule[];
}

export interface TimeFrameObject {
  startTime: Date;
  endTime: Date;
  id?: string;
  notes?: string;
}

export interface FilterCardProps {
  header: string;
  filterValue?: string;
  setFilterValue: (value: string) => void;
  options?: { value: string; label: string }[];
  isLabelSearchBy?: boolean;
  dropdownMinWidth?: number;
}

export interface Title {
  text: string;
  fontSize: number;
  fontWeight: string;
  fontColor: Gradient;
}

export interface Offer {
  publisherOfferId: string;
  offerId?: string;
  name: string;
  displayName?: string;
  description?: string | null;
  type: OfferType;
  subType?: EPopupSubType;
  priority?: number;
  indexToCollect?: number;
  activeMissionIndex?: number;
  collectedPoints?: number;
  offerUiId?: string;
  offerUi?: {
    offerUiId?: string;
    _id?: string;
    active: boolean;
    offerUiType: string;
    name: string;
    description: string;
    backgroundImage: string;
    specialOffer: SpecialOffer;
    badgeCoverImage?: string;
    progressBar?: ProgressBar;
    lockedStateButtonText?: string;
  };
  lockedStateButtonText?: string;
  active: boolean;
  coolDownInHours: number;
  segments: string[];
  schedule?: {
    permanent: boolean;
    timeFrames: TimeFrameObject[];
    interval?: string;
  };
  tags?: string[];
  productsSequence:
    | ProductsSequence[]
    | PopUpProductSequence[]
    | RollingOfferProductSequence[];
  createdBy: 'API';
  createdAt?: string;
  priceDiscount?: PriceDiscount;
  productSale?: ProductSale;
  badges?: OfferBadge[];
  playerAvailability?: number;
  isAllowedToCollect?: boolean;
  triggers: Array<TriggersObject>;
  displayRule?: {
    frequency: number;
    cooldownInMinutes: number;
  };
  showAfter?: any;
  section?: Partial<SectionsItem> | null;
  sectionId?: Partial<SectionsItem> | null;
  tab?: Partial<TabsItem> | null;
  tabId?: Partial<TabsItem> | null;
  startOver?: boolean;
  /** Runtime calendar behavior config (distinct from OfferUI.rewardCalendar visuals). */
  rewardCalendar?: {
    resetBehavior?: ERewardCalendarMissedReset;
    popupTrigger?: ERewardCalendarPopUpTrigger;
    rewardCollectionBehavior?: ERewardCalendarRewardCollection;
  };
}

export interface ConvertedPriceParams {
  row: {
    productsSequence: ProductsSequence[];
  };
}

export enum EPublisherSolutionModel {
  STORE = 'webStore',
  CHECKOUT = 'independentCheckout'
}

export enum EPublisherSolutionModelSubType {
  PAYMENT_LINKS = 'paymentLinks',
  WEB_CHECKOUT = 'webCheckout',
  SIDE_LOADING = 'sideLoading'
}

export interface PublisherInfo {
  _id: string;
  companyName: string;
  phoneNumberPrefix: string;
  phoneNumber: number;
  address: string;
  supportMail: string;
  supportImageUrl: string;
  thumbnailImageUrl?: string;
  domain: string;
  active: boolean;
  activeStore: boolean;
  storeTabName: string;
  publisherSolutionModel: EPublisherSolutionModel;
  solutionModelSubtype: EPublisherSolutionModelSubType;
  awardMail: string;
  createdAt: string;
  updatedAt: string;
  tags?: string[];
  showOnboardWidget?: boolean;
  themes?: EThemes[];
  invoiceMerchant?: string;
}

export interface Section {
  publisherSectionId: string;
  name: string;
  image?: string;
  sectionId?: string;
  offerType?: ESectionOfferType;
}

export interface Tab {
  publisherTabId: string;
  name: string;
  tabId?: string;
  iconUrl?: string;
}

export interface ScheduleTimeFrame {
  id?: string;
  startTime: Date;
  endTime: Date;
  notes?: string;
}

export type TimeframeDateRange = [dayjs.Dayjs | null, dayjs.Dayjs | null];

export interface TimeFrameTableData extends ScheduleTimeFrame {
  status: string;
  id: string;
}

export interface ScheduleTimeFrames {
  timeFrames?: ScheduleTimeFrame[];
}

export type ExtendedDateRange = [Date, Date, string?];

export type ExtendedAppearanceStatus =
  | EAppearanceStatus
  | EPendingAppearanceStatus;

interface ScheduleOption {
  value: string;
}

export interface AcSwitchSelectorProps {
  options: ScheduleOption[];
  onClick: (value: string) => void;
  value?: EScheduleType;
}

export interface PromoCode {
  id?: string;
  _id?: string;
  name: string;
  maxRedemptions: number;
  active: boolean;
  fresh?: boolean;
  promoCodeAvailability: [PromoCodeAvailability];
  couponId?: string;
}

export interface Coupon {
  id: string;
  name: string;
  active: boolean;
  discountPercentage: number;
  maxRedemptionsPerCustomer: number;
  maxRedemptions: number;
  currentRedemptions: number;
  expiredBy?: string | null;
  createdAt: string;
  supportedOffersBySku: string[];
  supportedOffersByType: string[];
  startsAt?: string | null;
  firstTimePurchase: boolean;
  promoCodes: PromoCode[];
  allowedPlayers: string[];
  promoCodeNames?: string[];
  updatedAt?: string;
}

export interface GetCouponsPaginatedResponse {
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  page: number;
  limit: number;
  data: Coupon[];
}

export enum EChecklistTaskStatus {
  OPEN = 'open',
  DONE = 'done',
  PENDING = 'pending',
  LOCKED = 'locked',
  IN_PROGRESS = 'in-progress'
}

export enum ETaskId {
  EDIT_CHECKOUT_IN_BUILDER = 'edit-checkout-in-builder',
  VERIFY_BUSINESS_CONTACT = 'verify-business-contact',
  READ_DOCUMENTATION = 'read-documentation',
  TEST_PAYMENTS = 'test-payments',
  SIGN_AGREEMENT = 'sign-agreement',
  GO_LIVE = 'go-live',
  CUSTOMIZE_STORE_LAYOUT = 'customize-store-layout',
  CUSTOMIZE_GENERAL_STORE_SETTINGS = 'customize-general-store-settings',
  CUSTOMIZE_STORE_PAGE = 'customize-store-page',
  CUSTOMIZE_CHECKOUT = 'customize-checkout',
  CUSTOMIZE_SUCCESS_PAGE = 'customize-success-page',
  REVIEW_AUTH_API_DOCS = 'review-auth-api-docs',
  CUSTOMIZE_AUTH_API = 'customize-auth-api',
  CONFIGURE_SIGNIN_METHODS = 'configure-signin-methods',
  CUSTOMIZE_LOGIN_PAGE = 'customize-login-page',
  REVIEW_ORDER_REPORTING_DOCS = 'review-order-reporting-docs',
  SETUP_AWARD_API = 'setup-award-api',
  CREATE_FIRST_PRODUCT = 'create-first-product',
  CREATE_OFFER_DESIGN = 'create-offer-design',
  ACTIVATE_FIRST_BUNDLE = 'activate-first-bundle',
  CREATE_PRICE_POINTS = 'create-price-points',
  REVIEW_PERSONALIZATION_DOCS = 'review-personalization-docs',
  SETUP_PERSONALIZATION_API = 'setup-personalization-api',
  REVIEW_DOMAIN_DOCS = 'review-domain-docs',
  CUSTOMIZE_YOUR_DOMAIN = 'customize-your-domain',
  CUSTOMIZE_RECEIPT = 'customize-receipt',
  REVIEW_CHECKOUT_SESSION_DOCS = 'review-checkout-session',
  CREATE_CHECKOUT_SESSION = 'create-checkout-session',
  REVIEW_FE_SDK_DOCS = 'review-fe-sdk-docs',
  REVIEW_AWARD_API_DOCS = 'review-award-api-docs',
  USE_GET_PRICE_POINTS = 'use-get-price-points'
}

export const TaskCategories = {
  CHECKOUT: 'Customize your checkout',
  VERIFY_BUSINESS: 'Verify your business',
  DOCUMENTATION: 'Read our documentation',
  TEST_PAYMENTS: 'Test payments',
  SIGN_AGREEMENT: 'Sign agreement',
  GO_LIVE: 'Go live',
  TEMPLATE_STUDIO: 'Template Studio',
  BUILDER: 'Builder',
  AUTHENTICATION: 'Authentication',
  PLAYER_ORDER_REPORTING_INTEGRATION: 'Player Order Reporting Integration',
  OFFERS: 'Offers',
  PRICE_POINTS: 'Price Points',
  PERSONALIZATION_API: 'Personalization API',
  GOING_LIVE: 'Going live',
  CREATE_CHECKOUT_SESSION_API: 'Create Checkout Session API',
  FRONTEND_SDK: 'Frontend SDK'
};

export interface ChecklistTask {
  id: ETaskId;
  status: EChecklistTaskStatus;
  locked?: boolean;
}

export interface PromoCodeAvailability {
  couponId: string;
  createdAt: string;
  currAvailability: number;
  maxRedemptions: number;
  promoCodeId: string;
  publisherId: string;
  redemptions: number;
  updatedAt: string;
}

export interface CouponConfiguration {
  enabled: boolean;
}

interface SubcategoryMap {
  [eventKey: string]: EEventSubCategories;
}

export const categorySubCategories: Record<string, SubcategoryMap> = {
  [EEventCategories.WEB_STORE]: {
    login_land: EEventSubCategories.LOGIN,
    login_method_selected: EEventSubCategories.LOGIN,
    login_canceled: EEventSubCategories.LOGIN,
    login_resolved: EEventSubCategories.LOGIN,
    shop_land: EEventSubCategories.STORE,
    offers_loaded: EEventSubCategories.STORE,
    offers_shown: EEventSubCategories.STORE,
    invalid_property_error: EEventSubCategories.ERROR,
    approval_request_closed: EEventSubCategories.CHECKOUT_LINKS,
    approval_request_displayed: EEventSubCategories.CHECKOUT_LINKS,
    'webstore.login.login_method.selected': EEventSubCategories.LOGIN,
    'webstore.login.canceled': EEventSubCategories.LOGIN,
    'webstore.login.displayed': EEventSubCategories.LOGIN,
    'webstore.login.completed': EEventSubCategories.LOGIN,
    'webstore.offers.loaded': EEventSubCategories.STORE,
    'webstore.offers.displayed': EEventSubCategories.STORE,
    'webstore.offers.popup_closed': EEventSubCategories.STORE,
    'webstore.store.displayed': EEventSubCategories.STORE,
    'webstore.personalization_api.error.invalid_property':
      EEventSubCategories.ERROR,
    'webstore.personalization_api.error': EEventSubCategories.ERROR,
    'webstore.approval_request.displayed': EEventSubCategories.CHECKOUT_LINKS,
    'webstore.approval_request.closed': EEventSubCategories.CHECKOUT_LINKS,
    'webstore.offers.progress_bar.points.granted':
      EEventSubCategories.PROGRESS_BAR,
    'webstore.offers.progress_bar.mission.started':
      EEventSubCategories.PROGRESS_BAR,
    'webstore.offers.progress_bar.mission.completed':
      EEventSubCategories.PROGRESS_BAR,
    'webstore.offers.popup_triggered': EEventSubCategories.STORE,
    'webstore.offers.badge_clicked': EEventSubCategories.STORE,
    'webstore.offer.clicked': EEventSubCategories.STORE
  },
  [EEventCategories.CHECKOUT]: {
    order_created: EEventSubCategories.ORDER,
    payment_intent_success: EEventSubCategories.ORDER,
    payment_intent_failed: EEventSubCategories.ORDER,
    order_completed_success: EEventSubCategories.ORDER,
    order_completed_failed: EEventSubCategories.ORDER,
    order_canceled: EEventSubCategories.ORDER,
    order_refunded: EEventSubCategories.DISPUTE,
    order_dispute_open: EEventSubCategories.DISPUTE,
    order_dispute_won: EEventSubCategories.DISPUTE,
    order_dispute_lost: EEventSubCategories.DISPUTE,
    'order.created': EEventSubCategories.ORDER,
    'order.payment.resolved': EEventSubCategories.ORDER,
    'order.completed': EEventSubCategories.ORDER,
    'order.canceled': EEventSubCategories.ORDER,
    'order.refunded': EEventSubCategories.DISPUTE,
    'order.dispute.opened': EEventSubCategories.DISPUTE,
    'order.dispute.resolved': EEventSubCategories.DISPUTE,
    'order.refund.failed': EEventSubCategories.ORDER
  },
  [EEventCategories.PORTAL]: {
    'portal.login.displayed': EEventSubCategories.LOGIN,
    'portal.login.login_method.selected': EEventSubCategories.LOGIN,
    'portal.login.canceled': EEventSubCategories.LOGIN,
    'portal.login.completed': EEventSubCategories.LOGIN,
    'portal.page.displayed': EEventSubCategories.PORTAL,
    'portal.content.displayed': EEventSubCategories.PORTAL,
    'portal.footer.clicked': EEventSubCategories.PORTAL,
    'portal.block.clicked': EEventSubCategories.PORTAL
  }
};

export interface SideBarV2Item {
  title: string;
  url: string;
  icon: React.ComponentType<any>;
}

export interface ReleaseConfiguration {
  message: string;
  fallbackUrl: string;
  fallbackMessage: string;
}

export interface TestRules {
  customerIds: string[];
  countryCodes: string[];
}

export enum VersionStatus {
  DISABLED = 'DISABLED',
  INACTIVE = 'INACTIVE',
  TESTING = 'TESTING',
  SOFT_RELEASE = 'SOFT_RELEASE',
  RELEASED = 'RELEASED'
}

export enum PackageStatus {
  READY = 'Ready for testing',
  TESTING = 'Testing active',
  GRADUAL_RELEASE = 'Gradual release active',
  ACTIVE = 'Full release active',
  INACTIVE = 'Release inactive'
}

export enum TestStatus {
  READY = 'READY TO TEST',
  IN_PROGRESS = 'TEST IN PROGRESS',
  NEW_VERSION_AVAILABLE = 'NEW VERSION AVAILABLE'
}

export enum ReleaseStatus {
  BLOCKED = 'RELEASE BLOCKED',
  READY = 'READY TO RELEASE',
  IN_PROGRESS = 'RELEASE IN PROGRESS',
  NEW_VERSION_AVAILABLE = 'NEW VERSION AVAILABLE'
}

export interface VersionStatuses {
  packageStatus: PackageStatus;
  testStatus: TestStatus;
  releaseStatus: ReleaseStatus;
}

export interface Version {
  publisherId?: string;
  versionCode: string;
  versionName: string;
  packageName: string;
  status: VersionStatus;
  releaseConfiguration?: ReleaseConfiguration;
  testRules?: TestRules;
  createdAt?: string;
  updatedAt?: string;
  isAppchargeApproved?: boolean;
  versionStatuses?: VersionStatuses;
}

export interface Store {
  storeId: string;
  storeName: string;
  isParkingDomain?: boolean;
}

export interface CreatePlan {
  displayName: string; // external name
  name: string; // internal name
  description?: string;
  basePriceInUsd: number;
  billingPeriodUnit: BillingPeriodUnit;
  billingCyclesUnit: BillingCyclesUnit;
  billingCycles?: number;
  firstPaymentDiscount?: number;
}

export interface Plan {
  _id?: string;
  publisherId: string;
  familyId: string;
  planId: string;
  billingCycles: number;
  name: string;
  displayName: string;
  description?: string;
  trialPeriod: number;
  billingPeriodUnit: BillingPeriodUnit;
  trialPeriodUnit: TrialPeriodUnit;
  basePriceInUsd: number;
  status: PlanStatus;
  billingCyclesUnit: BillingCyclesUnit;
  createdAt?: Date;
  updatedAt?: Date;
  providerPlanId: string;
  providerPlanName: string;
  coupon: PlanCoupon;
}

export interface GetPlanResponse {
  plans: Plan[];
  totalCount: number;
}

export interface UpdatePlan {
  name: string;
  displayName: string;
  billingPeriodUnit: string;
  billingCyclesUnit: string;
  billingCycles?: number;
  basePriceInUsd: number;
  firstPaymentDiscount?: number;
}

export interface GetSubscriptionResponse {
  subscriptions: Subscription[];
  totalCount: number;
}

export interface Subscription {
  _id: string;
  customerId: string;
  customerEmail?: string;
  billingPeriodUnit?: BillingPeriodUnit;
  nextBillingDate?: Date;
  lifeTimeValueInUsd?: number;
  successfulBillings?: number;
  status: SubscriptionStatus;
  createdAt: Date;
  publisherId: string;
}

export enum TrialPeriodUnit {
  DAYS = 'day',
  MONTHS = 'month'
}

export enum PlanStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived'
}

export enum BillingPeriodUnit {
  DAILY = 'day',
  WEEKLY = 'week',
  MONTHLY = 'month',
  YEARLY = 'year'
}

export enum BillingCyclesUnit {
  FIXED_NUMBER_OF_PAYMENTS = 'Fixed number of payments',
  ONGOING = 'Ongoing'
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  CANCELED = 'canceled',
  EXPIRED = 'expired',
  OVERDUE = 'overdue',
  TRIAL = 'trial',
  PENDING = 'pending'
}

export interface PlanCoupon {
  _id: string;
  status: string;
  providerCouponId: string;
  providerCouponName: string;
  discountType: string;
  discountPercentage: number;
  durationType: string;
  appliesOn: string;
}

export interface IOnePlusManyInstanceConfig {
  desktopLayout: ETemplateStudioLayoutOptions;
  themes: EThemes[];
  selectedDesignId: string;
  maxOffers: {
    unlimited: boolean;
    value: number;
  };
  rewardsLayout: 1 | 2 | 3;
}

export interface TemplateStudioModel {
  templateId: string;
  isGeneralTemplate: boolean;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  version: string;

  templateName: string;
  description?: string;
  tags?: string[];
  bodyBlocksOrder: BodyBlockId[];
  bodyBlocksOrderV2: BodyBlockV2Item[];
  storeBlocks?: TStoreBlock[];
  header: {
    showLogo: boolean;
    showPlayerName: boolean;
    showPlayerId: boolean;
    isSticky: boolean;
    backgroundOpacity: number;
    textColor: Gradient;
  };
  promotion: {
    isCarouselView: boolean;
    slidesPerViewDesktop: 1 | 2 | 3;
    themes: EThemes[];
    selectedDesignId: string;
  };
  bundle: {
    offersPerRow: ETemplateStudioBundlesViewModel | null;
    bundleViewModel: ETemplateStudioBundlesViewModel;
    themes: EThemes[];
    selectedDesignId: string;
  };
  popup: {
    selectedDesignId: EDailyBonusInternalViewModel;
  };
  rollingOffers: {
    desktopLayout: ETemplateStudioLayoutOptions;
    themes: EThemes[];
    selectedDesignId: string;
  };
  progressBar: {
    selectedDesignId: string;
  };
  richText?: {
    text: string;
    fontColor: string;
  };
  dailyBonusBadge: {
    themes: EThemes[];
    selectedDesignId: string;
  };
  triggeredPopups?: {
    selectedDesignId: string;
    selectedBadgeDesignId: string;
    themes: EThemes[];
  };
  rewardCalendar: {
    selectedDesignId: string;
    selectedBadgeDesignId: string;
    themes: EThemes[];
  };
  footer: {
    showAppchargeLogo: boolean;
    backgroundOpacity: number;
    textColor: Gradient;
  };
  brandKit: {
    fontFamily: string;
    buttonColor: Gradient;
    buttonTextColor: string;
    logo: string;
    backgroundImageDesktop: string;
    backgroundImageMobile: string;
    defaultBanner: string;
    footerDesktopImage: string;
    footerMobileImage: string;
    customArrowImage?: string;
  };
  externalButtons: {
    verticalOffset: number;
    addToHomeScreenButton: {
      active: boolean;
      borderColor: Gradient;
      backgroundColor: Gradient;
      buttonImage?: string;
      iconImage: string;
      shortcutName?: string;
    };
    backToGameButton: {
      active: boolean;
      buttonImage: string;
      position: EPosition;
    };
  };
}

export type TemplateStudioModelWithInstances = TemplateStudioModel &
  Partial<Record<OnePlusManyInstanceId, IOnePlusManyInstanceConfig>>;

export interface GamePortalPageModel {
  pageName: string;
  pageIcon: string;
  slug: string;
  pageTemplate: EPagesTemplateOptions;
  priority: number;
  subPagePriority: number;
  visibility: EPagesVisibilityOptions;
  metadataImage: string;
  metadataDescription: string;
  state: EPagesRadioButtonOptions;
  publishDate: string;
  isPublishDateEnabled: boolean;
  isSubPage: boolean;
  parentPageId?: string | null;
  blocksV2?: IBlock[];
}
